import {
    Avatar,
    InfoCard,
    Link,
} from '@backstage/core-components';
import { useEntity, getEntityRelations, EntityRefLinks } from '@backstage/plugin-catalog-react';
import {
    RELATION_CHILD_OF,
    RELATION_PARENT_OF,
    GroupEntity,
    ANNOTATION_EDIT_URL,
} from '@backstage/catalog-model';
import {
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    makeStyles,
} from '@material-ui/core';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import GroupIcon from '@material-ui/icons/Group';
import React from 'react';

const useStyles = makeStyles(theme => ({
    infoCard: {
        height: '100%',
        '& .MuiCardContent-root': {
            height: '100%',
        },
    },
    listItem: {
        paddingTop: 4,
        paddingBottom: 4,
    },
    listItemIcon: {
        minWidth: 40,
        color: theme.palette.primary.main,
    },
    secondaryText: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    avatar: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: theme.spacing(2),
    },
}));

type Props = {
    variant?: 'gridItem' | 'default' | 'fullHeight';
};

export const CustomGroupProfileCard = ({ variant }: Props) => {
    const classes = useStyles();
    const { entity: group } = useEntity<GroupEntity>();

    const entityMetadataEditUrl =
        group.metadata.annotations?.[ANNOTATION_EDIT_URL];

    const displayName =
        group.spec?.profile?.displayName ||
        group.metadata.title ||
        group.metadata.name;

    const email = group.spec?.profile?.email;
    const picture = group.spec?.profile?.picture;
    const description = group.metadata.description;

    const parentRelations = getEntityRelations(group, RELATION_CHILD_OF, {
        kind: 'group',
    });

    const childRelations = getEntityRelations(group, RELATION_PARENT_OF, {
        kind: 'Group',
    });

    return (
        <InfoCard
            className={classes.infoCard}
            title={
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GroupIcon fontSize="small" />
                    {displayName}
                </span>
            }
            subheader={description}
            variant={variant}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} sm={2} xl={1} className={classes.avatar}>
                    <Avatar displayName={displayName} picture={picture} />
                </Grid>

                <Grid item md={10} xl={11}>
                    <List dense>
                        {email && (
                            <>
                                <ListItem className={classes.listItem}>
                                    <ListItemIcon className={classes.listItemIcon}>
                                        <Tooltip title="E-mail">
                                            <EmailIcon />
                                        </Tooltip>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Link to={`mailto:${email}`}>{email}</Link>
                                        }
                                        secondary={
                                            <span className={classes.secondaryText}>E-mail</span>
                                        }
                                    />
                                </ListItem>
                                <Divider component="li" />
                            </>
                        )}

                        <ListItem className={classes.listItem}>
                            <ListItemIcon className={classes.listItemIcon}>
                                <Tooltip title="Grupo Pai">
                                    <AccountTreeIcon />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    parentRelations.length > 0 ? (
                                        <EntityRefLinks
                                            entityRefs={parentRelations}
                                            defaultKind="group"
                                        />
                                    ) : (
                                        'N/A'
                                    )
                                }
                                secondary={
                                    <span className={classes.secondaryText}>Grupo Pai</span>
                                }
                            />
                        </ListItem>

                        <Divider component="li" />

                        <ListItem className={classes.listItem}>
                            <ListItemIcon className={classes.listItemIcon}>
                                <Tooltip title="Subgrupos">
                                    <GroupIcon />
                                </Tooltip>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    childRelations.length > 0 ? (
                                        <EntityRefLinks
                                            entityRefs={childRelations}
                                            defaultKind="group"
                                        />
                                    ) : (
                                        'N/A'
                                    )
                                }
                                secondary={
                                    <span className={classes.secondaryText}>Subgrupos</span>
                                }
                            />
                        </ListItem>
                    </List>
                </Grid>
            </Grid>
        </InfoCard>
    );
};
